import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Chip,
  Autocomplete
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import VehicleDetailModal from "./components/VehicleDetailModal";

function VehicleManagement() {
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [status, setStatus] = useState('pending');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [search, setSearch] = useState('');

  const [subCategories, setSubCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(null);


  const getSubCategories = useCallback(async (parentId = null) => {
    try {
      const params = { limit: 0, };
      if (parentId) params.parent_id = parentId;
      const headers = {};

      const response = await apiRequest.get(endPoints.GET_SUB_CATEGORIES, params, headers);

      if (response.success) {
        const { items } = response.data;
        setSubCategories(items || []);
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
      setError("Failed to fetch sub categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch vehicles with pagination
  const getPendingVehicles = useCallback(
    async (page, search, categoryId) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems, status: status, search, category_id: categoryId };

        const response = await apiRequest.get(endPoints.GET_VEHICLES, params);

        if (response.success) {
          const { items, pagination } = response.data;
          setPendingVehicles(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        setError("Failed to fetch vehicles. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [totalItems, status]
  );

  useEffect(() => {
    getSubCategories();
    setAccessToken(localStorage.getItem("access_token"));
    getPendingVehicles(currentPage, search, categoryId);
  }, [currentPage, status, getPendingVehicles, search, getSubCategories, categoryId]);

  // const handleDelete = async (id) => {
  //   try {
  //     setLoading(true);
  //     setError(null);
  //     setSuccess(null);

  //     const response = await apiRequest.delete(
  //       `${endPoints.DELETE_VEHICLE}/${id}`,
  //       {
  //         headers: {
  //           'x-access-token': accessToken,
  //         },
  //       }
  //     );

  //     if (response.success) {
  //       setSuccess("Vehicle deleted successfully.");
  //       setPendingVehicles((prev) => prev.filter((vehicle) => vehicle.id !== id));
  //     } else {
  //       setError(response.message || "Failed to delete the vehicle.");
  //     }
  //   } catch (error) {
  //     console.error("Error deleting vehicle:", error);
  //     setError("An error occurred while deleting the vehicle.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleStatusToggle = async (id, currentStatus, reason = null) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus = currentStatus;
      const payload = { id, status: newStatus, reason: reason };
      console.log(payload);

      const headers = {
        'Content-Type': 'application/json',
        'x-access-token': accessToken,
      };

      const response = await apiRequest.put(
        `/v1/admin/driver/vehicle/status`,
        // `${endPoints.STATUS_VEHICLE}/${id}`,
        payload,
        { headers }
      );

      if (response.success) {
        setSuccess("Vehicle status updated successfully.");
        setPendingVehicles((prev) =>
          prev.map((vehicle) =>
            vehicle.id === id ? { ...vehicle, status: newStatus } : vehicle
          )
        );
        handleCloseDetailModal();
      } else {
        setError(response.message || 'Failed to update vehicle status.');
      }
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      setError('An error occurred while updating the vehicle status.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetail = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedVehicle(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'info';
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Vehicle Management
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant={status === 'pending' ? 'contained' : 'outlined'}
                      color="info"
                      onClick={() => setStatus('pending')}
                    >
                      Pending
                    </MDButton>
                    <MDButton
                      variant={status === 'approved' ? 'contained' : 'outlined'}
                      color="success"
                      onClick={() => setStatus('approved')}
                    >
                      Approved
                    </MDButton>
                    <MDButton
                      variant={status === 'rejected' ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => setStatus('rejected')}
                    >
                      Rejected
                    </MDButton>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <Autocomplete
                      options={subCategories}
                      getOptionLabel={(option) => option?.name_en || ""}
                      value={subCategories.find((c) => c.id === categoryId) || null}
                      onChange={(e, value) => setCategoryId(value?.id || null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter by Category"
                          variant="outlined"
                          size="small"
                          sx={{ width: 300 }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <TextField
                      label="Search"
                      variant="outlined"
                      size="small"
                      placeholder="Search vehicles..."
                      sx={{ width: 300 }}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </MDBox>
                </MDBox>
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Name</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Category</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Sub Category</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Contact</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Online</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Status</MDTypography></TableCell>
                        <TableCell><MDTypography variant="subtitle2" fontWeight="bold">Actions</MDTypography></TableCell>
                      </TableRow>
                      {pendingVehicles.map((vehicle) => (
                        <TableRow key={vehicle.id}>
                          <TableCell>
                            <MDBox display="flex" alignItems="center">
                              <Avatar
                                src={`${apiRequest.BASE_URL}/${vehicle.user?.profile}`}
                                sx={{ width: 36, height: 36, mr: 1 }}
                              />
                              <MDTypography variant="button" fontWeight="medium">
                                {vehicle.user?.full_name}
                              </MDTypography>
                            </MDBox>
                          </TableCell>
                          <TableCell>
                            {vehicle?.category_id}
                            {/* {vehicle.category.parent_categories?.map((cat, index) => (
                              <div key={index}>
                                {cat.name_en}
                              </div>
                            ))} */}
                          </TableCell>
                          <TableCell>
                            <MDTypography variant="caption" color="text">
                              {vehicle.category?.name_en}
                            </MDTypography>
                          </TableCell>

                          <TableCell>
                            <MDTypography variant="caption">
                              {vehicle.user?.contact}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={vehicle?.user?.is_online ? "Online" : "Offline"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={vehicle.status}
                              color={getStatusColor(vehicle.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <MDBox display="flex" gap={1}>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => handleDetail(vehicle)}
                              >
                                Details
                              </MDButton>
                              {/* <Switch
                                checked={vehicle.status === "active"}
                                onChange={() => handleStatusToggle(vehicle.id, vehicle.status)}
                                color="success"
                              /> */}
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages} {/* Display current page and total pages */}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Vehicle Detail Modal */}
      <VehicleDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        selectedVehicle={selectedVehicle}
        formatDate={formatDate}
        onApprove={() => {
          setCurrentId(selectedVehicle.id);
          setApprovalDialogOpen(true);
        }}
        onReject={() => {
          setCurrentId(selectedVehicle.id);
          setRejectionDialogOpen(true);
        }}
      />


      {/* Approval Confirmation Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)}>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve this vehicle?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              handleStatusToggle(currentId, 'approved');
              setApprovalDialogOpen(false);
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)}>
        <DialogTitle>Reason for Rejection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this vehicle:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="rejection-reason"
            label="Reason"
            type="text"
            fullWidth
            variant="standard"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              handleStatusToggle(currentId, 'rejected', rejectionReason);
              setRejectionDialogOpen(false);
              setRejectionReason('');
            }}
            color="primary"
            disabled={!rejectionReason}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default VehicleManagement;
